import {
  ChangeDetectionStrategy,
  Component,
  Input,
  signal,
  computed,
  OnInit,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { TranslateService } from '@ngx-translate/core';

import {
  AddFormComponent,
  FormField,
  ActionButton,
} from '@shared/components/add-form/add-form.component';
import {
  RetailStoreFormConfig,
  RETAIL_STORE_FORM_CONFIG,
  convertSalesOrganizationToOptions,
} from '../../retail-store.config';
import { PhysicalShopControllerService } from '@app/api/channel-service/api/physicalShopController.service';
import { SalesOrganizationControllerService } from '@app/api/channel-service/api/salesOrganizationController.service';
import { BlobConverterUtil } from '@shared/utils/blob-converter.util';
import { ListOfTreeVO } from '@app/api/channel-service/model/listOfTreeVO';
import { PageRequestDTO } from '@app/api/channel-service/model/pageRequestDTO';
import { PhysicalShopShowDTO } from '@app/api/channel-service/model/physicalShopShowDTO';
import { ChannelService } from '../../../service/channel.service';
import { ProvinceService } from '@app/feature/center-business/channel/service/province.service';
import { WarehouseOptionsService } from '@shared/services/warehouse-options.service';
import { PaginatedDropdownLoadParams } from '@shared/utils/paginated-dropdown.util';
import { map } from 'rxjs/operators';

@Component({
  selector: 'app-retail-store-form-wrapper',
  standalone: true,
  imports: [CommonModule, ToastModule, AddFormComponent],
  providers: [MessageService],
  templateUrl: './retail-store-form-wrapper.component.html',
  styleUrls: ['./retail-store-form-wrapper.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RetailStoreFormWrapperComponent implements OnInit {
  @Input() config!: RetailStoreFormConfig;
  @Input() editMode = false;
  @Input() viewMode = false;
  // Form data management
  formData = signal<any>({});
  originalApiData = signal<any>(null); // Store original data from API

  // Loading state
  loading = signal(false);
  initialLoading = signal(true);

  // Dropdown data
  companyOptions = signal<Array<{ label: string; value: number }>>([]);
  provinceOptions = signal<Array<{ label: string; value: number }>>([]);
  
  // Track current store ID and assigned warehouse IDs
  currentStoreId = signal<number | null>(null);
  assignedWarehouseIds = signal<Set<number>>(new Set());

  // Form component reference
  @ViewChild(AddFormComponent) addFormComponent?: AddFormComponent;

  // Storage key for form data
  private readonly FORM_DATA_STORAGE_KEY = 'retail_store_form_draft';
  private skipCanDeactivate = false;

  // Computed property to combine all form data for initialData (like SPU pattern)
  combinedInitialData = computed(() => {
    const baseData = {
      ...this.formData(),
    };

    return baseData;
  });

  // Computed property to get form fields with translated default values and dropdown options
  translatedFormFields = computed(() => {
    if (!this.config?.formFields) return [];

    return this.config.formFields.map((field) => {
      let updatedField = { ...field };

      // Handle translated default values
      if (field.defaultValue && typeof field.defaultValue === 'string') {
        const translatedValue = this.translateService.instant(
          field.defaultValue
        );
        if (translatedValue !== field.defaultValue) {
          updatedField = { ...updatedField, defaultValue: translatedValue };
        }
      }

      // Handle edit mode restrictions - make code field readonly in edit mode
      if (this.editMode && field.controlName === 'code') {
        updatedField = {
          ...updatedField,
          readonly: true,
          disabled: true,
        };
      }

      // Handle dropdown options
      if (
        field.controlName === 'salesOrganizingId' &&
        field.type === 'dropdown'
      ) {
        updatedField = { ...updatedField, options: this.companyOptions() };
      }

      if (
        field.controlName === 'provinceId' &&
        field.type === 'searchableDropdown'
      ) {
        updatedField = {
          ...updatedField,
          paginatedOptions: {
            loadPage: (params: PaginatedDropdownLoadParams) =>
              this.provinceService.getProvinceOptionsPage(params),
          },
        };
      }

      if (field.controlName === 'channelWarehouseName') {
        const assignedIds = this.assignedWarehouseIds();
        const currentWarehouseId = this.originalApiData()?.physicalWarehouseId
          ? Number(this.originalApiData()!.physicalWarehouseId)
          : null;
        updatedField = {
          ...updatedField,
          type: 'searchableDropdown',
          paginatedOptions: {
            loadPage: (params: PaginatedDropdownLoadParams) =>
              this.warehouseOptionsService
                .getOptionsPage(params, {
                  valueKey: 'id',
                  typeCodes: ['STORE'],
                })
                .pipe(
                  map((page) => ({
                    ...page,
                    options: page.options.filter((option) => {
                      const id = Number(option.value);
                      return (
                        !assignedIds.has(id) ||
                        (currentWarehouseId !== null &&
                          id === currentWarehouseId)
                      );
                    }),
                  })),
                ),
          },
        };
      }

      return updatedField;
    });
  });

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private physicalShopService: PhysicalShopControllerService,
    private salesOrganizationService: SalesOrganizationControllerService,
    // private warehouseService: WarehouseControllerService,
    private messageService: MessageService,
    private translateService: TranslateService,
    private channelService: ChannelService,
    private provinceService: ProvinceService,
    private readonly warehouseOptionsService: WarehouseOptionsService,
  ) {}

  ngOnInit(): void {
    // Load dropdown data first
    this.loadDropdownData();

    // Load existing data if in edit mode
    if (this.editMode || this.viewMode) {
      this.loadExistingData();
    } else {
      // For add mode, restore form data if returning from warehouse creation
      setTimeout(() => {
        this.restoreFormDataFromStorage();
        this.initialLoading.set(false);
      }, 500);
    }
  }

  private loadDropdownData(): void {
    // Load sales organization data
    this.salesOrganizationService.listOfTree(false).subscribe({
      next: async (response: any) => {
        try {
          let data: ListOfTreeVO[];

          // Handle blob response
          if (response instanceof Blob) {
            const jsonData = await BlobConverterUtil.convertToJson(response);
            data = jsonData || [];
          } else {
            data = response || [];
          }

          this.companyOptions.set(convertSalesOrganizationToOptions(data));
        } catch (error) {
          console.error('Error processing sales organization response:', error);
          this.companyOptions.set([]);
        }
      },
      error: (error: any) => {
        console.error('Error loading sales organization:', error);
        this.companyOptions.set([]);
      },
    });

    this.loadAssignedWarehouses();
  }

  /**
   * Load all retail stores to get list of assigned warehouse IDs
   */
  private loadAssignedWarehouses(): void {
    const pageRequest: PageRequestDTO = {
      page: 0,
      size: 200,
    };

    const physicalShopDTO: PhysicalShopShowDTO = {
      name: '',
      typeCode: '',
      manager: '',
      status: undefined, // Get all stores regardless of status
      selfPickupFlag: undefined,
      deleteFlag: false,
      pageRequest: pageRequest,
    };

    this.physicalShopService.showPage(physicalShopDTO, 'body', false).subscribe({
      next: async (response: any) => {
        try {
          let data: any[];

          // Handle blob response
          if (response instanceof Blob) {
            const jsonData = await BlobConverterUtil.convertToJson(response);
            data = jsonData?.pageContent || [];
          } else {
            data = response?.pageContent || [];
          }

          // Extract assigned warehouse IDs (exclude current store if editing)
          const assignedIds = new Set<number>();
          const currentId = this.currentStoreId();

          data.forEach((store: any) => {
            // Skip current store's warehouse assignment when editing
            if (store.id !== currentId && store.physicalWarehouseId) {
              assignedIds.add(Number(store.physicalWarehouseId));
            }
          });

          this.assignedWarehouseIds.set(assignedIds);
        } catch (error) {
          console.error('Error processing retail stores response:', error);
          this.assignedWarehouseIds.set(new Set());
        }
      },
      error: (error: any) => {
        console.error('Error loading retail stores:', error);
        this.assignedWarehouseIds.set(new Set());
      },
    });
  }

  private loadExistingData(): void {
    const storeId = this.route.snapshot.params['id'];

    if (storeId) {
      const storeIdNum = Number(storeId);
      this.currentStoreId.set(storeIdNum);

      this.physicalShopService.showDetails(storeIdNum).subscribe({
        next: async (response: any) => {
          try {
            // Handle blob response
            if (response instanceof Blob) {
              const jsonData = await BlobConverterUtil.convertToJson(response);
              this.loadFormDataFromApi(jsonData);
            } else {
              // Direct JSON response
              this.loadFormDataFromApi(response);
            }

            // Small delay to ensure form component is ready
            setTimeout(() => {
              this.initialLoading.set(false);
            }, 100);
          } catch (error) {
            console.error('Error processing store response:', error);
            this.initialLoading.set(false);
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to load store data',
            });
          }
        },
        error: (error: any) => {
          console.error('Error loading retail store:', error);
          this.initialLoading.set(false);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load store data',
          });
        },
      });
    } else {
      console.error('No store ID found in route parameters');
    }
  }

  private loadFormDataFromApi(apiItem: any): void {
    // Store original API data for merging during save
    this.originalApiData.set(apiItem);
    // Load form data (like SPU pattern)
    const formDataToSet = {
      name: apiItem.name || '',
      code: apiItem.code || '',
      typeCode: this.translateService.instant('retailStore.types.general_shop'), // Display label for UI
      address: apiItem.address || '',
      manager: apiItem.manager || '',
      salesOrganizingId: apiItem.salesOrganizingId || undefined, // Will be set from dropdown
      invoiceTypeCode: this.translateService.instant(
        'retailStore.invoice_types.vat_invoice'
      ),
      description: apiItem.description || '',
      countryId: apiItem.countryId || undefined,
      provinceId: apiItem.provinceId || undefined,
      cityId: apiItem.cityId || undefined,
      countyId: apiItem.countyId || undefined,
      // Dropdown stores physicalWarehouseId as value
      channelWarehouseName: apiItem.physicalWarehouseId || undefined,
      channelWarehouseStatus: apiItem.channelWarehouseStatus || true,
      selfPickupFlag: apiItem.selfPickupFlag || false,
      hasShipper: apiItem.hasShipper || false,
      deleteFlag: false,
      tenantId: 1,
    };
    
    // Reload assigned warehouses and available warehouses after setting originalApiData
    // This ensures the current store's warehouse is excluded from assigned list
    this.loadAssignedWarehouses();
    
    this.formData.set(formDataToSet);
  }

  onFormAction(event: { action: string; data?: any }): void {
    if (event.action === 'save') {
      if (this.isFormInvalid()) {
        this.markInvalidFormControls();
        this.messageService.add({
          severity: 'info',
          summary: this.translateService.instant('common.messages.info'),
          detail: this.translateService.instant(
            'retailStore.form.validation.required_fields'
          ),
        });
        return;
      }

      // Prevent multiple API calls if already saving
      if (this.loading()) {
        return;
      }
      // Set loading state immediately to prevent race conditions
      this.loading.set(true);
      this.saveRetailStoreData(event.data);
    } else if (event.action === 'cancel') {
      this.router.navigate([this.config.listRoute]);
    }
  }

  canDeactivate(): boolean {
    if (this.viewMode || this.skipCanDeactivate || this.loading()) {
      return true;
    }
    return !(this.addFormComponent?.form?.dirty ?? false);
  }

  private isFormInvalid(): boolean {
    const form = this.addFormComponent?.form;
    if (!form) {
      return true;
    }

    form.updateValueAndValidity({ emitEvent: false });
    return form.invalid;
  }

  private markInvalidFormControls(): void {
    const form = this.addFormComponent?.form;
    if (!form) {
      return;
    }

    form.markAllAsTouched();
    Object.keys(form.controls).forEach((key) => {
      const control = form.get(key);
      if (control?.invalid) {
        control.markAsDirty();
        control.updateValueAndValidity({ emitEvent: false });
      }
    });
    this.addFormComponent?.markForCheck();
  }

  private saveRetailStoreData(formData: any): void {
    try {
      const storeId = this.route.snapshot.params['id'];

      if (this.editMode && storeId) {
        // Get original API data to merge with form changes
        const originalData = this.originalApiData();

        // Update existing store - merge original data with form changes
        const shopDTO = {
          // Always include ID from route
          id: Number(storeId),

          // Merge form data with original data (form data takes precedence)
          name: formData.name || originalData?.name || '',
          code: formData.code || originalData?.code || '',
          address: formData.address || originalData?.address || '',
          manager: formData.manager || originalData?.manager || '',

          // Server-side fields (always send these values)
          typeCode: 'basic', // Hardcoded as requested - always send 'basic' to server
          invoiceTypeCode: 'vat-invoice', // Hardcoded as requested - always send 'vat-invoice' to server

          // Merge other fields with original data
          salesOrganizingId:
            formData.salesOrganizingId || originalData?.salesOrganizingId || 1,
          description: formData.description || originalData?.description || '',
          countryId: formData.countryId || originalData?.countryId || undefined,
          provinceId:
            formData.provinceId || originalData?.provinceId || undefined,
          cityId: formData.cityId || originalData?.cityId || undefined,
          countyId: formData.countyId || originalData?.countyId || undefined,
          // Get warehouse name from selected id
          channelWarehouseName: this.getWarehouseNameFromId(
            formData.channelWarehouseName || originalData?.physicalWarehouseId
          ),
          physicalWarehouseId:
            formData.channelWarehouseName ||
            originalData?.physicalWarehouseId ||
            undefined,
          selfPickupFlag:
            formData.selfPickupFlag !== undefined
              ? formData.selfPickupFlag
              : originalData?.selfPickupFlag || false,
          hasShipper:
            formData.hasShipper !== undefined
              ? formData.hasShipper
              : originalData?.hasShipper || false,
          deleteFlag: false,
          tenantId: 1,

          // Include any other fields from original data that might not be in the form
          ...(originalData && {
            // Preserve any additional fields from the original API response
            status: originalData.status,
            createdBy: originalData.createdBy,
            creationDate: originalData.creationDate,
            lastUpdatedBy: originalData.lastUpdatedBy,
            lastUpdateDate: originalData.lastUpdateDate,
            objectVersionNumber: originalData.objectVersionNumber,
          }),
        };

        this.physicalShopService
          .save(shopDTO, 'body', false, {
            httpHeaderAccept: 'application/json' as any,
          })
          .subscribe({
            next: () => {
              this.loading.set(false); // Reset loading state
              this.skipCanDeactivate = true;
              this.addFormComponent?.form?.markAsPristine();
              this.messageService.add({
                severity: 'success',
                summary: this.translateService.instant(
                  'common.messages.success'
                ),
                detail: this.translateService.instant(
                  'retailStore.messages.update_success'
                ),
              });
              // Navigate back to list
              setTimeout(
                () => this.router.navigate([this.config.listRoute]),
                1000
              );
            },
            error: (error: any) => {
              this.loading.set(false); // Reset loading state
              console.error('Error updating store:', error);
              this.messageService.add(
                this.channelService.handleBackendValidationError(
                  error,
                  'update'
                )
              );
            },
          });
      } else {
        // Create new store (for add mode)
        const shopDTO = {
          name: formData.name || '',
          code: formData.code || '',
          address: formData.address || '',
          manager: formData.manager || '',
          typeCode: 'basic',
          salesOrganizingId: formData.salesOrganizingId || 1,
          invoiceTypeCode: 'vat-invoice',
          description: formData.description || '',
          countryId: formData.countryId || undefined,
          provinceId: formData.provinceId || undefined,
          cityId: formData.cityId || undefined,
          countyId: formData.countyId || undefined,
          // Get warehouse name from selected id
          channelWarehouseName: this.getWarehouseNameFromId(
            formData.channelWarehouseName
          ),
          physicalWarehouseId: formData.channelWarehouseName || undefined,
          selfPickupFlag: formData.selfPickupFlag || false,
          hasShipper: formData.hasShipper || false,
          deleteFlag: false,
          tenantId: 1,
        };

        this.physicalShopService
          .save(shopDTO, 'body', false, {
            httpHeaderAccept: 'application/json' as any,
          })
          .subscribe({
            next: () => {
              this.loading.set(false); // Reset loading state
              this.skipCanDeactivate = true;
              this.addFormComponent?.form?.markAsPristine();
              this.messageService.add({
                severity: 'success',
                summary: this.translateService.instant(
                  'common.messages.success'
                ),
                detail: this.translateService.instant(
                  'retailStore.messages.create_success'
                ),
              });
              // Navigate back to list
              setTimeout(
                () => this.router.navigate([this.config.listRoute]),
                1000
              );
            },
            error: (error: any) => {
              this.loading.set(false); // Reset loading state
              console.error('Error creating store:', error);
              this.messageService.add(
                this.channelService.handleBackendValidationError(
                  error,
                  'create'
                )
              );
            },
          });
      }
    } catch (error) {
      this.loading.set(false); // Reset loading state
      console.error('Error saving store data:', error);
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.messages.error'),
        detail: this.translateService.instant(
          'retailStore.messages.create_error'
        ),
      });
    }
  }

  /**
   * Get warehouse name from id
   */
  private getWarehouseNameFromId(id: number | string | undefined): string {
    if (!id) return '';
    const apiData = this.originalApiData();
    if (
      apiData?.physicalWarehouseId &&
      Number(apiData.physicalWarehouseId) === Number(id)
    ) {
      return apiData.physicalWarehouseName || '';
    }
    return '';
  }

  /**
   * Handle section action (e.g., create store warehouse button)
   */
  onSectionAction(event: { section: string; action: string; data?: any }): void {
    if (event.action === 'create_store_warehouse') {
      // Save current form data to localStorage before redirecting
      this.saveFormDataToStorage();
      
      // Navigate to create logical warehouse page with typeCode = 'STORE'
      this.router.navigate(['/warehouse/logical/add'], {
        queryParams: { 
          typeCode: 'STORE',
          returnUrl: this.router.url // Store current URL to return to
        },
      });
    }
  }

  /**
   * Save current form data to localStorage
   */
  private saveFormDataToStorage(): void {
    try {
      const form = (this.addFormComponent as any)?.form;
      if (form) {
        const formValue = form.getRawValue();
        localStorage.setItem(
          this.FORM_DATA_STORAGE_KEY,
          JSON.stringify(formValue)
        );
      }
    } catch (error) {
      console.error('Error saving form data to storage:', error);
    }
  }

  /**
   * Restore form data from localStorage
   */
  private restoreFormDataFromStorage(): void {
    try {
      const savedData = localStorage.getItem(this.FORM_DATA_STORAGE_KEY);
      if (savedData) {
        const formData = JSON.parse(savedData);
        
        // Wait for form to be initialized
        const checkForm = () => {
          const form = (this.addFormComponent as any)?.form;
          if (form && formData) {
            // Restore form values
            Object.keys(formData).forEach((key) => {
              const control = form.get(key);
              if (control) {
                control.setValue(formData[key], { emitEvent: false });
              }
            });
            // Clear storage after restoring
            localStorage.removeItem(this.FORM_DATA_STORAGE_KEY);
          } else {
            // Retry after a short delay if form is not ready
            setTimeout(checkForm, 100);
          }
        };
        
        checkForm();
      }
    } catch (error) {
      console.error('Error restoring form data from storage:', error);
      // Clear corrupted data
      localStorage.removeItem(this.FORM_DATA_STORAGE_KEY);
    }
  }
}
